import { useEffect, useState } from 'react';
import { getFestivalIdFromSubdomain } from '../../services/api';

/** The subdomain that may name a festival ("doxart" in doxart.backoffice.festival.systems); none on localhost or an IP */
const festivalSubdomain = (hostname: string): string | null => {
  if (hostname === 'localhost' || /^[\d.]+$/.test(hostname)) return null;
  const subdomain = hostname.split('.')[0];
  return subdomain !== hostname ? subdomain : null;
};

/**
 * The festival the sign-in and password reset screens act on. The link's `festival_id` comes first, then the festival
 * of the subdomain: both lock it. Otherwise the last festival signed in to is suggested, and the user can change it.
 */
export const useFestivalId = (search: string) => {
  const urlFestivalId = new URLSearchParams(search).get('festival_id');
  const subdomain = urlFestivalId ? null : festivalSubdomain(window.location.hostname);

  const [festivalId, setFestivalId] = useState(urlFestivalId ?? '');
  const [isLocked, setIsLocked] = useState(!!urlFestivalId);
  const [isResolving, setIsResolving] = useState(!!subdomain);

  useEffect(() => {
    if (urlFestivalId) {
      setFestivalId(urlFestivalId);
      setIsLocked(true);
      setIsResolving(false);
      return;
    }

    const suggestLastUsed = () => {
      const lastUsedFestivalId = localStorage.getItem('lastUsedFestivalId');
      if (lastUsedFestivalId) setFestivalId(lastUsedFestivalId);
    };
    if (!subdomain) {
      suggestLastUsed();
      return;
    }

    let cancelled = false;
    setIsResolving(true);
    getFestivalIdFromSubdomain(subdomain)
      .then(festivalIdFromSubdomain => {
        if (cancelled) return;
        if (festivalIdFromSubdomain) {
          setFestivalId(festivalIdFromSubdomain);
          setIsLocked(true);
        } else {
          suggestLastUsed();
        }
      })
      .catch(() => !cancelled && suggestLastUsed())
      .finally(() => !cancelled && setIsResolving(false));
    return () => {
      cancelled = true;
    };
  }, [urlFestivalId, subdomain]);

  return {
    festivalId,
    setFestivalId,
    /** Set by the link or the subdomain: not the user's to change */
    isLocked,
    /** Looking the subdomain's festival up */
    isResolving,
  };
};
